import React from 'react';
import { Box, Typography, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
import { StyledTypography } from '../styles/styles';
import { Palette } from 'Palette';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

interface CompactCardSelectorProps {
  label: string;
  required?: boolean;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  helperText?: string;
  layout?: 'horizontal' | 'vertical';
  cardSize?: 'small' | 'medium' | 'large';
}

// --- Styled Components ---

const StyledSelectCard = styled(Card, {
  shouldForwardProp: (prop) => !['selected', 'hasError', 'cardHeight'].includes(prop as string),
})<{ selected?: boolean; hasError?: boolean; cardHeight?: string }>(
  ({ theme, selected = false, hasError = false, cardHeight = '200px' }) => ({
    cursor: 'pointer',
    border: `1px solid ${
      hasError ? theme.palette.error.main : selected ? Palette.tone2.buttonBg : '#ccc'
    }`,
    backgroundColor: selected ? Palette.default.buttonBg : Palette.tone1.background,
    color: selected ? Palette.default.buttonText : Palette.tone1.text,
    transition: 'all 0.2s ease-in-out',
    height: cardHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
    '&:hover': {
      borderColor: hasError ? theme.palette.error.main : Palette.tone1.buttonBg,
      backgroundColor: selected ? Palette.default.buttonBg : Palette.default.background,
      transform: 'none',
      boxShadow: 'none',
    },
  }),
);

const ContentColumn = styled(Box, {
  shouldForwardProp: (prop) => !['selected', 'hasError'].includes(prop as string),
})<{ selected?: boolean; hasError?: boolean }>(({ theme, selected = false, hasError = false }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  color: hasError
    ? theme.palette.error.main
    : selected
      ? Palette.default.buttonText
      : Palette.default.text,
  padding: '1rem',
}));

// --- Component ---

export const CompactCardSelector: React.FC<CompactCardSelectorProps> = ({
  label,
  required = false,
  options,
  value,
  onChange,
  error,
  helperText,
  layout = 'horizontal',
  cardSize = 'medium',
}) => {
  const hasError = Boolean(error?.trim());

  // Calculate card height based on content
  const hasIcons = options.some((option) => option.icon);
  const hasDescriptions = options.some((option) => option.description);

  const getCardHeight = () => {
    // Base heights for different card sizes
    const baseHeights = {
      small: {
        labelsOnly: '120px',
        withIcons: '140px',
        withDescriptions: '160px',
        withBoth: '180px',
      },
      medium: {
        labelsOnly: '200px',
        withIcons: '240px',
        withDescriptions: '240px',
        withBoth: '280px',
      },
      large: {
        labelsOnly: '240px',
        withIcons: '280px',
        withDescriptions: '280px',
        withBoth: '320px',
      },
    };

    const sizeHeights = baseHeights[cardSize];

    if (hasIcons && hasDescriptions) {
      return sizeHeights.withBoth;
    } else if (hasIcons || hasDescriptions) {
      return sizeHeights.withDescriptions;
    } else {
      return sizeHeights.labelsOnly;
    }
  };

  const getCardSizing = () => {
    switch (cardSize) {
      case 'small':
        return { flex: '0 0 auto', minWidth: '100px', width: 'calc(15% - 8px)' };
      case 'large':
        return { flex: '0 0 auto', minWidth: '200px', width: 'calc(33.333% - 8px)' };
      default:
        return { flex: '0 0 auto', minWidth: '120px', width: 'calc(20% - 8px)' };
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <StyledTypography
        variant="body1"
        fontWeight={600}
        color={hasError ? 'error' : undefined}
        gutterBottom
      >
        {label}
        {required && ' *'}
      </StyledTypography>

      <Box
        display="flex"
        flexDirection={layout === 'vertical' ? 'column' : 'row'}
        gap={1}
        flexWrap={layout === 'horizontal' ? 'wrap' : 'nowrap'}
      >
        {options.map((option) => {
          const isSelected = value === option.value;

          return (
            <StyledSelectCard
              key={option.value}
              selected={isSelected}
              hasError={hasError}
              cardHeight={getCardHeight()}
              onClick={() => onChange(option.value)}
              sx={getCardSizing()}
            >
              <ContentColumn selected={isSelected} hasError={hasError}>
                {option.icon && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    {option.icon}
                  </Box>
                )}

                <StyledTypography
                  variant="body2"
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    fontSize: cardSize === 'small' ? '0.9rem' : '1rem',
                    color: isSelected ? Palette.default.buttonText : Palette.default.text,
                  }}
                >
                  {option.label}
                </StyledTypography>

                {option.description && (
                  <StyledTypography
                    variant="caption"
                    color={isSelected ? Palette.default.buttonText : Palette.default.text}
                    sx={{
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                      hyphens: 'auto',
                      padding: '0 0.5rem',
                    }}
                  >
                    {option.description}
                  </StyledTypography>
                )}
              </ContentColumn>
            </StyledSelectCard>
          );
        })}
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
      {!error && helperText && (
        <Typography variant="caption" color={Palette.default.text} sx={{ mt: 0.5 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default CompactCardSelector;
